import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, Chip, Stack, Skeleton,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Menu, MenuItem, Divider, List, ListItem, ListItemText, ListItemSecondaryAction,
  Tabs, Tab
} from '@mui/material'
import { MoreVert, Share, Edit, ExitToApp, ContentCopy, Add, Delete, PersonAdd, Email } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { Family, Invite, AdditionalFamilyMember, SendInAppInviteResult } from '@/types'

export default function FamilyDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [family, setFamily] = useState<Family | null>(null)
  const [loading, setLoading] = useState(true)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [invite, setInvite] = useState<Invite | null>(null)
  const [generatingInvite, setGeneratingInvite] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [newMemberName, setNewMemberName] = useState('')
  const [addingMember, setAddingMember] = useState(false)
  const [inviteTab, setInviteTab] = useState(0)
  const [emailInvite, setEmailInvite] = useState('')
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false)

  const loadFamily = useCallback(async () => {
    if (!id) return
    try {
      const data = await apiClient.get<Family>(`/api/families/${id}`)
      setFamily(data)
    } catch {
      enqueueSnackbar('Failed to load family', { variant: 'error' })
      navigate('/families')
    } finally {
      setLoading(false)
    }
  }, [id, enqueueSnackbar, navigate])

  useEffect(() => { loadFamily() }, [loadFamily])

  const handleEdit = async () => {
    if (!editName.trim() || !id) return
    try {
      await apiClient.put(`/api/families/${id}`, { name: editName.trim() })
      setFamily(prev => prev ? { ...prev, name: editName.trim() } : null)
      setEditOpen(false)
      enqueueSnackbar('Family updated', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to update family', { variant: 'error' })
    }
  }

  const handleLeave = async () => {
    if (!id) return
    if (!confirm('Are you sure you want to leave this family?')) return
    try {
      await apiClient.post(`/api/families/${id}/leave`)
      enqueueSnackbar('Left family', { variant: 'success' })
      navigate('/families')
    } catch {
      enqueueSnackbar('Failed to leave family', { variant: 'error' })
    }
  }

  const handleGenerateInvite = async () => {
    if (!id) return
    setGeneratingInvite(true)
    try {
      const inv = await apiClient.post<Invite>(`/api/invites/family/${id}`, { expirationHours: 72 })
      setInvite(inv)
    } catch {
      enqueueSnackbar('Failed to generate invite', { variant: 'error' })
    } finally {
      setGeneratingInvite(false)
    }
  }

  const copyInviteLink = () => {
    if (!invite) return
    const link = `${window.location.origin}/invite/${invite.inviteCode}`
    navigator.clipboard.writeText(link)
    enqueueSnackbar('Invite link copied!', { variant: 'success' })
  }

  const handleSendEmailInvite = async () => {
    if (!emailInvite.trim() || !id) return
    setSendingEmailInvite(true)
    try {
      const result = await apiClient.post<SendInAppInviteResult>(`/api/invites/family/${id}/email`, { email: emailInvite.trim() })
      if (result.success) {
        enqueueSnackbar('Invite sent!', { variant: 'success' })
        setEmailInvite('')
      } else {
        enqueueSnackbar(result.error || 'Failed to send invite', { variant: 'error' })
      }
    } catch {
      enqueueSnackbar('Failed to send invite', { variant: 'error' })
    } finally {
      setSendingEmailInvite(false)
    }
  }

  const handleAddAdditionalMember = async () => {
    if (!newMemberName.trim() || !id) return
    setAddingMember(true)
    try {
      const member = await apiClient.post<AdditionalFamilyMember>(`/api/families/${id}/additional-members`, { name: newMemberName.trim() })
      setFamily(prev => prev ? { ...prev, additionalMembers: [...prev.additionalMembers, member] } : null)
      setNewMemberName('')
      setAddMemberOpen(false)
      enqueueSnackbar('Family member added', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to add family member', { variant: 'error' })
    } finally {
      setAddingMember(false)
    }
  }

  const handleRemoveAdditionalMember = async (memberId: string) => {
    if (!id) return
    if (!confirm('Remove this family member?')) return
    try {
      await apiClient.delete(`/api/families/${id}/additional-members/${memberId}`)
      setFamily(prev => prev ? { ...prev, additionalMembers: prev.additionalMembers.filter(m => m.id !== memberId) } : null)
      enqueueSnackbar('Family member removed', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to remove family member', { variant: 'error' })
    }
  }

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
      </Box>
    )
  }

  if (!family) return null

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{family.name}</Typography>
        <IconButton onClick={e => setMenuAnchor(e.currentTarget)}>
          <MoreVert />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); setEditName(family.name); setEditOpen(true) }}>
            <Edit sx={{ mr: 1 }} fontSize="small" /> Edit Family
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); setInviteOpen(true); handleGenerateInvite() }}>
            <Share sx={{ mr: 1 }} fontSize="small" /> Invite Members
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); handleLeave() }} sx={{ color: 'error.main' }}>
            <ExitToApp sx={{ mr: 1 }} fontSize="small" /> Leave Family
          </MenuItem>
        </Menu>
      </Box>

      {/* Members */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Members ({family.members.length})
      </Typography>
      <Stack spacing={1}>
        {family.members.map(member => (
          <Card key={member.userId} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/users/${member.userId}`)}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {member.displayName}
                {member.userId === user?.userId && (
                  <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">{member.email}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Additional Members */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Additional Members ({family.additionalMembers.length})
        </Typography>
        <Button size="small" startIcon={<PersonAdd />} onClick={() => setAddMemberOpen(true)}>
          Add
        </Button>
      </Box>
      {family.additionalMembers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No additional members yet.
        </Typography>
      ) : (
        <Card variant="outlined">
          <List disablePadding>
            {family.additionalMembers.map((member, index) => (
              <Box key={member.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText primary={member.name} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => handleRemoveAdditionalMember(member.id)} aria-label="remove member">
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit Family</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Family Name" value={editName}
            onChange={e => setEditName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleEdit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={!editName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => { setInviteOpen(false); setInvite(null); setInviteTab(0); setEmailInvite('') }} fullWidth maxWidth="xs">
        <DialogTitle>Invite to {family.name}</DialogTitle>
        <DialogContent>
          <Tabs value={inviteTab} onChange={(_, v) => setInviteTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<Share fontSize="small" />} iconPosition="start" label="Link" />
            <Tab icon={<Email fontSize="small" />} iconPosition="start" label="Email" />
          </Tabs>
          {inviteTab === 0 && (
            <>
              {generatingInvite ? (
                <Skeleton variant="rounded" height={48} />
              ) : invite ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Share this link with family members. Expires in 72 hours.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {window.location.origin}/invite/{invite.inviteCode}
                    </Typography>
                    <IconButton size="small" onClick={copyInviteLink}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ) : null}
            </>
          )}
          {inviteTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Send an in-app invite to a user by their email address.
              </Typography>
              <TextField
                autoFocus fullWidth label="Email Address" type="email" value={emailInvite}
                onChange={e => setEmailInvite(e.target.value)} margin="dense"
                onKeyDown={e => e.key === 'Enter' && handleSendEmailInvite()}
              />
              <Button
                onClick={handleSendEmailInvite} variant="contained" fullWidth sx={{ mt: 1 }}
                disabled={sendingEmailInvite || !emailInvite.trim()}
                startIcon={<PersonAdd />}
              >
                {sendingEmailInvite ? 'Sending...' : 'Send Invite'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteOpen(false); setInvite(null); setInviteTab(0); setEmailInvite('') }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Additional Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => { setAddMemberOpen(false); setNewMemberName('') }} fullWidth maxWidth="xs">
        <DialogTitle>Add Family Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add a family member by name. This can be anyone, not just app users.
          </Typography>
          <TextField
            autoFocus fullWidth label="Name" value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleAddAdditionalMember()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddMemberOpen(false); setNewMemberName('') }}>Cancel</Button>
          <Button onClick={handleAddAdditionalMember} variant="contained" disabled={addingMember || !newMemberName.trim()} startIcon={<Add />}>
            {addingMember ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
